import type { Config } from "./types.js";

export function defaultConfig(
  modelContextLimit: number,
  overrides: Partial<Config> = {},
): Config {
  const base: Config = {
    tiers: { enabled: true, tier2Trigger: 5, tier3Trigger: 10 },
    nudge: {
      maxContextLimitPct: 0.75,
      minContextLimitPct: 0.45,
      frequency: 5,
      iterationThreshold: 15,
      force: "soft",
      growthRatio: 0.05,
      growthFloor: 50000,
      growthCap: 50000,
      minGrowthFloor: 20000,
      minGrowthRatio: 0.45,
      emergencyThresholdPct: 0.95,
      tier2GrowthMultiplier: 1.5,
    },
    promotionThreshold: 5,
    truncate: { threshold: 0.95, terminalEscapeAfter: 3 },
    compress: {
      minCompressRange: 5000,
      maxSummaryLength: 20000,
      minSummaryLength: 50,
    },
    protectedTools: [],
    protectedLatestTools: [],
    preserveRecentMessages: 5,
    preserveRecentTokens: 5000,
    modelContextLimit,
    absorb: {
      enabled: false,
      toolName: "absorb",
      minToolTokens: 1000,
      contextThresholdPct: 0,
      excludeTools: [],
    },
  };
  return {
    ...base,
    ...overrides,
    tiers: { ...base.tiers, ...overrides.tiers },
    nudge: { ...base.nudge, ...overrides.nudge },
    truncate: { ...base.truncate, ...overrides.truncate },
    compress: { ...base.compress, ...overrides.compress },
    absorb: overrides.absorb
      ? { ...base.absorb, ...overrides.absorb }
      : base.absorb,
  };
}

export function validateConfig(config: Config): string[] {
  const errors: string[] = [];
  if (
    !Number.isFinite(config.modelContextLimit) ||
    config.modelContextLimit <= 0
  ) {
    errors.push("modelContextLimit must be a positive number");
  }
  if (config.nudge.minContextLimitPct > config.nudge.maxContextLimitPct) {
    errors.push(
      "nudge.minContextLimitPct must not exceed nudge.maxContextLimitPct",
    );
  }
  if (config.nudge.maxContextLimitPct > config.nudge.emergencyThresholdPct) {
    errors.push(
      "nudge.maxContextLimitPct must not exceed nudge.emergencyThresholdPct",
    );
  }
  if (
    config.nudge.minPressureBenefitTokens !== undefined &&
    (!Number.isFinite(config.nudge.minPressureBenefitTokens) ||
      config.nudge.minPressureBenefitTokens < 0)
  ) {
    errors.push("nudge.minPressureBenefitTokens must be finite and >= 0");
  }
  if (config.promotionThreshold < 1) {
    errors.push("promotionThreshold must be >= 1");
  }
  if (config.truncate.threshold <= 0 || config.truncate.threshold > 1) {
    errors.push("truncate.threshold must be in (0, 1]");
  }
  if (
    config.truncate.terminalEscapeAfter !== undefined &&
    (!Number.isInteger(config.truncate.terminalEscapeAfter) ||
      config.truncate.terminalEscapeAfter < 0)
  ) {
    errors.push("truncate.terminalEscapeAfter must be an integer >= 0");
  }
  for (const tier of [config.tiers.tier2Trigger, config.tiers.tier3Trigger]) {
    if (tier < 1) errors.push("tier triggers must be >= 1");
  }
  if (config.tiers.tier3Trigger <= config.tiers.tier2Trigger) {
    errors.push("tiers.tier3Trigger must be greater than tiers.tier2Trigger");
  }
  if (config.absorb) {
    if (config.absorb.enabled && !config.absorb.toolName) {
      errors.push("absorb.toolName must be a non-empty string when enabled");
    }
    if (
      !Number.isFinite(config.absorb.minToolTokens) ||
      config.absorb.minToolTokens < 0
    ) {
      errors.push("absorb.minToolTokens must be >= 0");
    }
    if (
      config.absorb.contextThresholdPct < 0 ||
      config.absorb.contextThresholdPct > 1
    ) {
      errors.push("absorb.contextThresholdPct must be in [0, 1]");
    }
  }
  if (config.rules) {
    if (
      config.rules.maxRules !== undefined &&
      (!Number.isFinite(config.rules.maxRules) || config.rules.maxRules < 1)
    ) {
      errors.push("rules.maxRules must be >= 1");
    }
    if (
      config.rules.maxRuleChars !== undefined &&
      (!Number.isFinite(config.rules.maxRuleChars) ||
        config.rules.maxRuleChars < 1)
    ) {
      errors.push("rules.maxRuleChars must be >= 1");
    }
  }
  return errors;
}
